// AutoCrop.jsx
// This helper automatically crops an image to a centered square (1:1 aspect ratio)
// and outputs a fixed-size image (default 500x500 pixels). This ensures your artlist cover
// always maintains a consistent square format.

import config from '@/config';

const autoCropImage = async (artworkImage, outputSize = 500) => {
  let imageUrl;
  
  // If artworkImage is a string (an image id), construct the URL using the image service domain.
  if (typeof artworkImage === 'string') {
    imageUrl = `${config.app.imageServiceDomain}/${artworkImage}/brightness=0.8`;
  } else {
    // Otherwise, try to use image_id or image_url from the object.
    imageUrl = artworkImage.image_id || artworkImage.image_url;
    // If the URL doesn't look complete, prepend the image service domain.
    if (imageUrl && !imageUrl.startsWith('http')) {
      imageUrl = `${config.app.imageServiceDomain}/${imageUrl}/brightness=0.8`;
    }
  }
    
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous'; // Ensures we can use the image in a canvas.
    image.src = imageUrl;
  
    image.onload = () => {
      const { width, height } = image;
      
      // Determine the crop size as the smallest dimension
      const cropSize = Math.min(width, height);
      // Calculate coordinates to center the crop
      const startX = (width - cropSize) / 2;
      const startY = (height - cropSize) / 2;
  
      // Create a canvas with fixed output dimensions.
      const canvas = document.createElement('canvas');
      canvas.width = outputSize;
      canvas.height = outputSize;
      const ctx = canvas.getContext('2d');
  
      // Draw the cropped portion of the image scaled to the fixed dimensions.
      ctx.drawImage(
        image,
        startX,       // source x
        startY,       // source y
        cropSize,     // source width
        cropSize,     // source height
        0,            // destination x
        0,            // destination y
        outputSize,   // destination width
        outputSize    // destination height
      );
  
      // Convert the canvas content into a Blob (JPEG format)
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          console.error('AutoCrop: Failed to generate blob.');
          reject(new Error('Auto-crop failed: Unable to generate image blob.'));
        }
      }, 'image/jpeg');
    };
  
    image.onerror = (error) => {
      console.error('AutoCrop: Error loading image:', error);
      reject(new Error('Auto-crop failed: Unable to load image. ' + error));
    };
  });
};
  
export default autoCropImage;
